import React from 'react';
import { useNavigate } from 'react-router-dom';
import ErrorPage403 from '@/app/routes/errorPage403';
import { Button } from '@/shared/components/Buttons';
import { ButtonVariant } from '@/shared/components/Buttons/types';
import InlineLayout from '@/shared/layout/inlineLayout';
import Loader from '@/app/routes/Loader';
import {
  useViewTechnician,
  PROFILE_NAV_ITEMS,
} from '../../hooks/useViewTechnician';
import ViewTechnicianContent from './viewTechnicianContent';
import ViewTechnicianComplianceDetails from './viewTechnicianComplianceDetails';
import ViewTechnicianFacilities from './viewTechnicianFacilities';
import ViewTechnicianPermissions from './viewTechnicianPermissions';
import ViewTechnicianErrorScreen from './viewTechnicianErrorScreen';

interface ViewTechnicianPageProps {
  technicianId: string;
}

const ViewTechnicianPage: React.FC<ViewTechnicianPageProps> = ({
  technicianId,
}) => {
  const navigate = useNavigate();
  const {
    technician,
    showErrorScreen,
    accessError,
    initials,
    statusLabel,
    statusBadgeClasses,
    isLoading,
  } = useViewTechnician(technicianId);

  const profileImageUrl =
    technician?.profile &&
    !Array.isArray(technician.profile) &&
    technician.profile.url
      ? technician.profile.url
      : undefined;

  const profileHeader = (
    <>
      <div className="flex items-center gap-4">
        <div
          className="flex items-center justify-center shrink-0 rounded-full bg-[#E8EEF0] text-[#223746] font-medium overflow-hidden"
          style={{
            width: '64px',
            height: '64px',
            fontSize: '22px',
            lineHeight: '32px',
          }}
        >
          {profileImageUrl ? (
            <img
              src={profileImageUrl}
              alt=""
              className="w-full h-full object-cover"
            />
          ) : (
            initials
          )}
        </div>
      </div>
      <div className="flex min-w-0 flex-1">
        <div className="flex min-w-0 flex-col gap-[4px]">
          <div className="flex min-w-0 flex-col items-start gap-1">
            <h2
              className="min-w-0 break-all font-medium text-neutral-10"
              style={{
                fontSize: '18px',
                lineHeight: '28px',
              }}
            >
              {technician?.name ?? '—'}
            </h2>
            <span
              className={`px-3 py-1 rounded-[8px] text-sm font-medium flex items-center gap-2 ${statusBadgeClasses.container}`}
              style={{
                fontSize: '14px',
                lineHeight: '20px',
              }}
            >
              <div
                className={`rounded-full shrink-0 ${statusBadgeClasses.dot}`}
                style={{
                  width: '6px',
                  height: '6px',
                }}
              />
              {statusLabel}
            </span>
          </div>
        </div>
      </div>
      <Button
        label="Edytuj Technika"
        variant={ButtonVariant.Primary}
        size="Medium"
        onClick={() => navigate(`/technicians/Edycja-technika/${technicianId}`)}
        className="py-[10px]! px-[16px]! font-medium! text-[14px]! hover:cursor-pointer mt-1  "
        style={{ borderRadius: '8px' }}
      />
    </>
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Loader />
      </div>
    );
  }

  if (accessError) {
    return <ErrorPage403 />;
  }

  return (
    <React.Fragment key={technicianId}>
      {showErrorScreen ? (
        <ViewTechnicianErrorScreen />
      ) : (
        <InlineLayout
          title="Podgląd technika"
          navItems={[...PROFILE_NAV_ITEMS]}
          contentByNavId={{
            PodstawoweInformacje: (
              <ViewTechnicianContent technician={technician ?? null} />
            ),
            ComplianceDetails: (
              <ViewTechnicianComplianceDetails
                technician={technician ?? null}
              />
            ),
            Facilities: <ViewTechnicianFacilities />,
            Permissions: (
              <ViewTechnicianPermissions technicianId={technicianId} />
            ),
          }}
          header={profileHeader}
          searchParamKey="tab"
          defaultActiveId="PodstawoweInformacje"
        />
      )}
    </React.Fragment>
  );
};

export default ViewTechnicianPage;
